import { getPrices, getPricesInsertion } from "@/api/cms";
import { resolveCmsIcon } from "@/lib/cms-puck/blocks/icons";
import {
  blockStyleNeedsCssRules,
  buildBlockCssRules,
  hasCustomBorderColor,
  normalizeBlockStyle,
  type BlockStyle,
} from "@/lib/cms-puck/block-style";
import {
  allSidesBorderWidthZero,
  buildHoverCss,
  hasCustomRadius,
  normalizeButtonAppearance,
  resolveButtonBoxCss,
  resolveButtonVariantClass,
  type ButtonAppearance,
  type ButtonVariant,
  type IconPosition,
} from "@/lib/cms-puck/blocks/button";
import {
  normalizeStripePaymentTarget,
  type StripePaymentTarget,
} from "@/lib/cms-puck/blocks/pricing-payment";
import { usePuckTheme } from "@/context/cms-puck/PuckThemeContext";
import { useBlockStyleCss } from "@/hooks/use-block-style-css";
import { cmsNew } from "@/lib/cms/cms-new-i18n";
import { useQuery } from "@tanstack/react-query";
import { useId } from "react";
import { TEXT_ALIGN_CLASS } from "@/components/cms-puck/blocks/shared";

/** Variante de bouton → classes Tailwind (parité BO ↔ front). */
export const BUTTON_VARIANT_CLASS = {
  primary: (suppress = false, suppressColor = false) =>
    resolveButtonVariantClass("primary", suppress, suppressColor),
  secondary: (suppress = false, suppressColor = false) =>
    resolveButtonVariantClass("secondary", suppress, suppressColor),
  outline: (suppress = false, suppressColor = false) =>
    resolveButtonVariantClass("outline", suppress, suppressColor),
  link: (suppress = false, suppressColor = false) =>
    resolveButtonVariantClass("link", suppress, suppressColor),
} as const;

/**
 * ButtonBlock — render du bloc Bouton en mode style `"self"` : le style du bloc
 * (fond/texte/typo/espacement) est appliqué INLINE sur le `<span>` lui-même afin
 * de BATTRE la classe de variante (sinon le fond resterait celui de la variante).
 * Composant dédié → `useBlockStyleCss` (hook) appelé hors du literal de config.
 */
export function ButtonBlock({
  id,
  label,
  variant,
  align,
  style,
  iconName,
  iconPosition = "before",
  appearance,
}: {
  id?: string;
  label: React.ReactNode;
  variant: ButtonVariant;
  align: "left" | "center" | "right";
  style?: BlockStyle;
  iconName?: string | null;
  iconPosition?: IconPosition;
  appearance?: ButtonAppearance;
}) {
  const theme = usePuckTheme();
  const palette = theme?.colors ?? [];
  const css = useBlockStyleCss(style);
  const appr = normalizeButtonAppearance(appearance);
  const boxCss = resolveButtonBoxCss(palette, appr.border, appr.radius);
  // Classe scopée stable (par bloc) → règle `:hover` injectée (inline impossible).
  const cls = `cms-btn-${id ?? "preview"}`;
  const hoverCss = buildHoverCss(cls, appr.hover, palette);
  const Icon = iconName ? resolveCmsIcon(iconName) : null;
  // Règles générées du style partagé (responsive/visibilité) : pas de wrapper en
  // mode « self » → le bouton porte lui-même la classe + le <style>, comme le
  // hover. Attributs (ID/classes) posés sur l'élément.
  const uid = useId();
  const normalized = normalizeBlockStyle(style);
  const needsRules = blockStyleNeedsCssRules(normalized);
  const blockClass = `cms-blk-${uid.replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const rules = needsRules
    ? buildBlockCssRules(`.${blockClass}`, normalized)
    : null;
  const attrs = normalized.attributes;
  const sharedRadius =
    normalized.design.radius.tl != null ||
    normalized.design.radius.tr != null ||
    normalized.design.radius.br != null ||
    normalized.design.radius.bl != null;
  const radiusClass =
    hasCustomRadius(appr.radius) || sharedRadius ? "" : "rounded";
  const suppressOutlineBorder =
    allSidesBorderWidthZero(normalized.design.border) ||
    allSidesBorderWidthZero(appr.border);
  const suppressOutlineBorderColor =
    hasCustomBorderColor(normalized.design.border) ||
    hasCustomBorderColor(appr.border);
  return (
    <div className={TEXT_ALIGN_CLASS[align]}>
      {hoverCss ? (
        <style dangerouslySetInnerHTML={{ __html: hoverCss }} />
      ) : null}
      {rules ? <style>{rules}</style> : null}
      <span
        id={attrs.cssId ?? undefined}
        className={`${cls} ${needsRules ? blockClass : ""} ${attrs.cssClass ?? ""} inline-flex items-center gap-2 ${radiusClass} px-5 py-2 text-sm font-medium transition-colors ${BUTTON_VARIANT_CLASS[variant](suppressOutlineBorder, suppressOutlineBorderColor)}`}
        // Style partagé (css) PAR-DESSUS l'apparence legacy (boxCss) : depuis
        // l'unification, les bordures/rayons s'éditent dans le Design partagé ;
        // l'apparence n'est prioritaire que si le style partagé est vide.
        style={{ ...(boxCss as React.CSSProperties), ...css }}
      >
        {Icon && iconPosition === "before" ? <Icon size="1em" /> : null}
        {label}
        {Icon && iconPosition === "after" ? <Icon size="1em" /> : null}
      </span>
    </div>
  );
}

/** Aperçu éditeur du bloc `PaymentButton` (style identique au bouton classique). */
export function PaymentButtonBlock({
  id,
  label,
  payment,
  variant,
  align,
  style,
  iconName,
  iconPosition = "before",
  appearance,
}: {
  id?: string;
  label: React.ReactNode;
  payment?: StripePaymentTarget;
  variant: "primary" | "secondary" | "outline";
  align: "left" | "center" | "right";
  style?: BlockStyle;
  iconName?: string | null;
  iconPosition?: IconPosition;
  appearance?: ButtonAppearance;
}) {
  const target = normalizeStripePaymentTarget(payment);
  const { data: prices = [] } = useQuery<
    Array<{
      id: string;
      priceDescriptionFromStripe?: {
        price?: { unit_amount?: number | string };
        product?: { name?: string };
      };
    }>
  >({
    queryKey: ["puck-payment-button-preview", target.mode, target.priceId],
    queryFn: () =>
      target.mode === "insertion" ? getPricesInsertion() : getPrices(),
    enabled: Boolean(target.priceId),
  });

  const linked = target.priceId
    ? prices.find((p) => p.id === target.priceId)
    : undefined;
  const stripeName = linked?.priceDescriptionFromStripe?.product?.name;
  const cents = linked?.priceDescriptionFromStripe?.price?.unit_amount;
  const amount =
    typeof cents === "string"
      ? parseInt(cents, 10)
      : typeof cents === "number"
        ? cents
        : 0;
  const priceHint =
    stripeName && Number.isFinite(amount) && amount > 0
      ? `${stripeName} (${amount / 100} €)`
      : stripeName
        ? stripeName
        : target.priceId
          ? cmsNew("cms.new.preview.pricing.priceNotFound", "Prix introuvable")
          : cmsNew("cms.new.preview.pricing.noPrice", "Aucun prix sélectionné");

  return (
    <div className="space-y-1">
      <ButtonBlock
        id={id}
        label={label}
        variant={variant}
        align={align}
        style={style}
        iconName={iconName}
        iconPosition={iconPosition}
        appearance={appearance}
      />
      <p className="text-center text-[10px] text-slate-400">
        {target.mode === "insertion" ? "Insertion" : "Consultation"} —{" "}
        {priceHint}
        {target.mode === "insertion" ? (
          <>
            {" "}
            → parcours expertise (<code>/expertise/introduction</code>)
          </>
        ) : null}
      </p>
    </div>
  );
}

/** Bouton « pilule » réutilisé par Cta (aperçu éditeur, non cliquable). */
export function PreviewButton({
  label,
  variant = "primary",
}: {
  label: React.ReactNode;
  variant?: "primary" | "secondary" | "outline";
}) {
  return (
    <span
      className={`inline-block rounded px-5 py-2 text-sm font-medium ${resolveButtonVariantClass(variant)}`}
    >
      {label}
    </span>
  );
}
